/**
 * Customer-side service catalog and tax calendar.
 *
 * Endpoints (verified against /openapi.json):
 *   GET /api/v1/customer/services/      → services available to this customer
 *   GET /api/v1/customer/tax-calendar/  → upcoming tax calendar events
 *   GET /api/v1/services                → public services catalog (auth optional)
 *   GET /api/v1/service-steps           → constants for service steps (used by client wizards)
 */
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import type { ApiError } from '@finmin/api-client';
import { createKeyFactory, type Schemas } from '@finmin/api-client';
import type { PanType } from '@finmin/utils';
import { apiClient } from '@/lib/api';

// ---------- types -----------------------------------------------------------

export type ServiceListResponse = Schemas['ServiceListResponse'];
export type ServiceResponse = Schemas['ServiceResponse'];
export type TaxCalendarResponse = Schemas['TaxCalendarResponse'];
export type TaxCalendarEvent = Schemas['TaxCalendarEvent'];
export type ServiceStepsListResponse = Schemas['ServiceStepsListResponse'];

/** Grouped catalog: hand-written until `pnpm gen:types` catches up. */
export interface GroupedService {
  service_type: string;
  name: string | null;
  /** Short qualifier that distinguishes same-named rows, e.g. "Revised Return". */
  details: string | null;
  description: string | null;
  price_min: number | null;
  price_max: number | null;
  expected_tat: string | null;
  /** PAN entity types this service is offered to ("for whom"). */
  pan_types?: PanType[];
}
export interface ServiceGroup {
  category: string;
  services: GroupedService[];
}
export interface GroupedServicesResponse {
  groups: ServiceGroup[];
}

// ---------- query keys ------------------------------------------------------

export const servicesKeys = createKeyFactory('services', {
  customerCatalog: () => ['customer-catalog'] as const,
  groupedCatalog: (panType?: string) => ['grouped-catalog', panType ?? 'all'] as const,
  publicCatalog: () => ['public-catalog'] as const,
  taxCalendar: () => ['tax-calendar'] as const,
  steps: () => ['steps'] as const,
});

// ---------- raw API functions -----------------------------------------------

/** GET /api/v1/customer/services/: services tailored to this customer (post-profile). */
export async function listCustomerServices(signal?: AbortSignal) {
  const { data } = await apiClient.get<ServiceListResponse>('/api/v1/customer/services/', { signal });
  return data;
}

/** GET /api/v1/customer/services/grouped: active catalog grouped by category, optionally filtered by PAN type. */
export async function listGroupedServices(signal?: AbortSignal, panType?: string) {
  const { data } = await apiClient.get<GroupedServicesResponse>(
    '/api/v1/customer/services/grouped',
    { signal, params: { pan_type: panType } },
  );
  return data;
}

/** GET /api/v1/customer/tax-calendar/: upcoming compliance dates for this customer. */
export async function getTaxCalendar(signal?: AbortSignal) {
  const { data } = await apiClient.get<TaxCalendarResponse>('/api/v1/customer/tax-calendar/', {
    signal,
  });
  return data;
}

/** GET /api/v1/services: public services catalog (used pre-login for marketing). */
export async function listPublicServices(signal?: AbortSignal) {
  const { data } = await apiClient.get<ServiceListResponse>('/api/v1/services', { signal });
  return data;
}

/** GET /api/v1/service-steps: enum-like constants for the service-request wizard. */
export async function listServiceSteps(signal?: AbortSignal) {
  const { data } = await apiClient.get<ServiceStepsListResponse>('/api/v1/service-steps', { signal });
  return data;
}

// ---------- hooks -----------------------------------------------------------

export function useCustomerServices(
  options?: Omit<UseQueryOptions<ServiceListResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ServiceListResponse, ApiError>({
    queryKey: servicesKeys.customerCatalog(),
    queryFn: ({ signal }) => listCustomerServices(signal),
    staleTime: 5 * 60_000, // catalog rarely changes
    ...options,
  });
}

export function useGroupedServices(
  panType?: string,
  options?: Omit<UseQueryOptions<GroupedServicesResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<GroupedServicesResponse, ApiError>({
    queryKey: servicesKeys.groupedCatalog(panType),
    queryFn: ({ signal }) => listGroupedServices(signal, panType),
    staleTime: 60_000,
    ...options,
  });
}

export function useTaxCalendar(
  options?: Omit<UseQueryOptions<TaxCalendarResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<TaxCalendarResponse, ApiError>({
    queryKey: servicesKeys.taxCalendar(),
    queryFn: ({ signal }) => getTaxCalendar(signal),
    staleTime: 60 * 60_000, // calendar updates daily at most
    ...options,
  });
}

export function usePublicServices(
  options?: Omit<UseQueryOptions<ServiceListResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ServiceListResponse, ApiError>({
    queryKey: servicesKeys.publicCatalog(),
    queryFn: ({ signal }) => listPublicServices(signal),
    staleTime: 5 * 60_000,
    ...options,
  });
}

export function usePublicServiceSteps(
  options?: Omit<UseQueryOptions<ServiceStepsListResponse, ApiError>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<ServiceStepsListResponse, ApiError>({
    queryKey: servicesKeys.steps(),
    queryFn: ({ signal }) => listServiceSteps(signal),
    staleTime: Infinity,
    ...options,
  });
}
